// medicalApiActions.js
import axios from 'axios';
import { BASE_URL } from '../../../../Constants/constants';

/**
 * Creates authorization configuration object for API requests
 * @param {boolean} isMultipart - Whether the request contains multipart form data
 * @returns {Object} Configuration object with authorization headers
 */
const getAuthConfig = (isMultipart = false) => ({
    headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        ...(isMultipart ? { 'Content-Type': 'multipart/form-data' } : {})
    }
});

/**
 * Fetches medical data for the current user
 * @param {Object} currentUser - Current user information
 * @returns {Promise<Object|null>} Medical data or null if not found
 * @throws {Error} If the request fails for reasons other than 404
 */
export const fetchMedicalData = async (currentUser) => {
    try {
        const response = await axios.get(`${BASE_URL}/medical/me`, getAuthConfig());
        console.log('Fetched medical data:', response.data);

        // Ensure we always return an array
        if (!response.data) {
            return [];
        }

        // If it's already an array, return it; otherwise, wrap it in an array
        return Array.isArray(response.data) ? response.data : [response.data];

    } catch (error) {
        if (error.response?.status === 404) {
            return [];
        }
        console.error('Error fetching medical data:', error);
        throw new Error('Failed to fetch medical information.');
    }
};
// export const fetchMedicalData = async (currentUser) => {
//     try {
//         const response = await axios.get(`${BASE_URL}/medical/me`, getAuthConfig());
//         console.log('Fetched medical data:', response.data);
//         return Array.isArray(response.data) ? response.data : [];
//     } catch (error) {
//         if (error.response?.status === 404) {
//             return [];
//         }
//         console.error('Error fetching medical data:', error);
//         throw new Error('Failed to fetch medical information.');
//     }
// };

// export const fetchMedicalData = async (currentUser) => {
//     try {
//         const response = await axios.get(`${BASE_URL}/medical/me`, getAuthConfig());
//         console.log('Fetched medical data:', response.data);
//         return response.data || [];
//     } catch (error) {
//         if (error.response?.status === 404) {
//             return [];
//         }
//         console.error('Error fetching medical data:', error);
//         throw new Error('Failed to fetch medical information.');
//     }
// };

/**
 * Saves or updates medical data for the current user
 * @param {Object} medical - Medical data to save
 * @param {Object} currentUser - Current user information
 * @param {Function} onSave - Callback when save is successful
 * @param {Function} setShowSuccessMessage - Callback to show success message
 * @param {Function} setErrors - Callback to set errors
 * @returns {Promise<Object>} Saved medical data
 * @throws {Error} If the save operation fails
 */
// In MedicalApiActions.js, enhance the saveMedicalData function:
export const saveMedicalData = async (medical, currentUser, onSave, setShowSuccessMessage, setErrors) => {
    try {
        console.log("Starting medical save process with data:", medical);

        if (!currentUser?.id) {
            throw new Error('Current user ID is required');
        }

        if (!medical?.blood_type?.trim()) {
            throw new Error('Blood type is required');
        }

        const url = medical.id
            ? `${BASE_URL}/medical/${medical.id}`
            : `${BASE_URL}/medical/`;

        console.log(`Sending ${medical.id ? 'PUT' : 'POST'} request to ${url}`);

        const response = await axios({
            method: medical.id ? 'put' : 'post',
            url,
            data: {
                ...medical,
                user_id: currentUser.id
            },
            ...getAuthConfig()
        });

        console.log("Medical save response:", response.data);

        // Get a fresh copy of all medical records after save
        const updatedRecords = await fetchMedicalData(currentUser);
        console.log("Fetched updated medical records:", updatedRecords);

        // Pass the ARRAY of records, not just a single record
        if (onSave && updatedRecords) {
            console.log("Calling onSave with array of records:", updatedRecords);
            onSave('medical', updatedRecords);
        }

        setShowSuccessMessage?.(true);

        return response.data;
    } catch (error) {
        console.error("Error saving medical data:", error);
        const errorMessage = error.response?.data?.detail ||
            error.message ||
            'Failed to save medical information.';

        setErrors?.([errorMessage]);
        throw error;
    }
};
/**
 * Deletes medical data for the specified ID
 * @param {string|number} medicalId - ID of the medical record to delete
 * @param {Object} currentUser - Current user information
 * @returns {Promise<Object>} Response data from the delete operation
 * @throws {Error} If the delete operation fails
 */
export const deleteMedicalData = async (medicalId, currentUser) => {
    try {
        const response = await axios.delete(
            `${BASE_URL}/medical/${medicalId}`,
            {
                ...getAuthConfig(),
                data: {
                    id: medicalId,
                    user_id: currentUser.id,
                    confirmation: true
                }
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error deleting medical data:', error);
        throw new Error('Failed to delete medical information.');
    }
};